/**
 * pw-giveaway-entry.js (2026-08-27) — giveaway entry form.
 * Contract: pagewright/docs/contracts/giveaway-entry.v1.md.
 *
 * Three things this does that a naive form wouldn't:
 *
 *  1. Captures MOUNT TIME and sends `elapsedMs`. The server silently swallows anything submitted
 *     under 2.5s. A human cannot type a name and an email that fast; a bot posts instantly.
 *  2. Treats `already_entered` as a SUCCESS. Telling someone who entered yesterday that something
 *     "failed" is the single most likely way this form generates support email.
 *  3. Swaps the form out for the success panel rather than just printing a line under it, so the
 *     page can't be resubmitted by reflex and the outcome is unmissable.
 *
 * No framework, no build step — same plain-IIFE shape as pw-freebie-claim.js.
 */
(function () {
  document.querySelectorAll('[data-pw-giveaway-form]').forEach(function (form) {
    var section = form.closest('[data-backend-url]');
    if (!section) return;

    var backendUrl = section.getAttribute('data-backend-url') || '';
    var campaign = section.getAttribute('data-campaign-slug') || '';
    var mountedAt = Date.now();

    var nameInput = form.querySelector('[data-pw-giveaway-name]');
    var emailInput = form.querySelector('[data-pw-giveaway-email]');
    var agreeInput = form.querySelector('[data-pw-giveaway-agree]');
    var newsletterInput = form.querySelector('[data-pw-giveaway-newsletter]');
    var hpInput = form.querySelector('[data-pw-giveaway-hp]');
    var submitBtn = form.querySelector('[data-pw-giveaway-submit]');
    var statusEl = form.querySelector('[data-pw-giveaway-status]');
    var successEl = section.querySelector('[data-pw-giveaway-success]');

    function say(message, isError) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.hidden = false;
      if (isError) statusEl.setAttribute('data-error', '');
      else statusEl.removeAttribute('data-error');
    }

    /** Replace the form with the branded panel. `bodyOverride` lets the duplicate case reuse the
     *  same panel with its own, friendlier line instead of inventing a second layout. */
    function showSuccessPanel(bodyOverride) {
      if (!successEl) {
        say(bodyOverride || section.getAttribute('data-success-body') || "You're in!", false);
        return;
      }
      if (bodyOverride) {
        var bodyEl = successEl.querySelector('[data-pw-giveaway-success-body]');
        if (bodyEl) bodyEl.textContent = bodyOverride;
      }
      form.hidden = true;
      successEl.hidden = false;
      // Move focus so a screen reader lands on the outcome rather than staying on a hidden button.
      successEl.setAttribute('tabindex', '-1');
      successEl.focus({ preventScroll: false });
    }

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();

      var name = nameInput ? nameInput.value.trim() : '';
      var email = emailInput ? emailInput.value.trim() : '';

      if (!name || !email) {
        say('Please fill in your name and email.', true);
        return;
      }
      if (agreeInput && !agreeInput.checked) {
        say('Please agree to the official rules.', true);
        return;
      }

      submitBtn.disabled = true;
      say('Entering…', false);

      fetch(backendUrl + '/api/giveaway-enter', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          campaign: campaign,
          name: name,
          email: email,
          agree: true,
          // Strict boolean from the OPTIONAL, unchecked-by-default box (contract v2 §2). Never a
          // constant: only a ticked box can subscribe anyone, and the entry itself is consent-free.
          newsletterOptIn: !!(newsletterInput && newsletterInput.checked),
          website: hpInput ? hpInput.value : '',
          elapsedMs: Date.now() - mountedAt,
        }),
      })
        .then(function (res) {
          return res
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { ok: res.ok, status: res.status, body: body };
            });
        })
        .then(function (result) {
          var code = result.body && result.body.code;
          var message = (result.body && result.body.message) || '';

          if (code === 'entered') {
            showSuccessPanel(null);
            // #1722: through the gate -- withheld on staging/preview themes (assets/pw-analytics-gate.js).
            if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('giveaway_entry', { campaign: campaign });
            return;
          }

          // A duplicate is a SUCCESS state. Never an error panel, never a red line.
          if (code === 'already_entered') {
            showSuccessPanel(section.getAttribute('data-already-body') || message);
            return;
          }

          // Before opening: dated, neutral copy, and the button stays usable (the visitor can retry once
          // it opens). Never the word "closed" (Fable HIGH-2, 2026-10-14).
          if (code === 'not_open') {
            say(section.getAttribute('data-not-open-body') || message, false); // neutral, not the red error style
            submitBtn.disabled = false;
            return;
          }

          if (code === 'closed') {
            say(section.getAttribute('data-closed-body') || message, true);
            return; // deliberately leaves the button disabled — there is nothing to retry
          }

          say(message || 'Something went wrong — please try again in a moment.', true);
          submitBtn.disabled = false;
        })
        .catch(function () {
          say('Something went wrong — please try again in a moment.', true);
          submitBtn.disabled = false;
        });
    });
  });
})();
